import { useRef, useState } from "react";
import { BadgeCheck, Check, Info, Trash2 } from "lucide-react";
import { saveCompanyCredentials, type Credential, type CredentialKind, type PrimeCompany } from "../api/CrmApi";
import { AMOUNT_KINDS, CORE_KINDS } from "../credentialNames";
import { apiErrorMessage } from "../apiError";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Alert from "shared/Alert";
import AppSelect from "shared/AppSelect";
import CredentialPicker from "./CredentialPicker";
import { FORM_CONTROL } from "shared/tableStyles";

export const MAX_CREDENTIALS = 60;
export const MAX_NAME_LENGTH = 120;
export const MAX_DETAIL_LENGTH = 300;
export const MAX_AMOUNT = 1_000_000_000_000;

const KIND_ORDER: CredentialKind[] = ["certificate", "licence", "bond", "insurance", "vehicle", "business_cert"];
const NAME_PLACEHOLDERS: Record<CredentialKind, string> = {
  certificate: "e.g. SOC 2, FedRAMP, ISO 27001",
  licence: "e.g. Professional Engineer (PE)",
  bond: "e.g. Bid bond, Performance bond",
  insurance: "e.g. General liability, Cyber liability",
  vehicle: "e.g. SAM.gov registration, GSA MAS",
  business_cert: "e.g. MBE, WBE, DBE, 8(a)",
};
const DETAIL_PLACEHOLDERS: Record<CredentialKind, string> = {
  certificate: "Detail (optional): scope, report date…",
  licence: "Detail (optional): licence number, state, scope…",
  bond: "Detail (optional): surety, notes…",
  insurance: "Detail (optional): carrier, policy notes…",
  vehicle: "Detail (optional): UEI / CAGE or contract number…",
  business_cert: "Detail (optional): state and certifying body…",
};

/** A whole-dollar amount typed or pasted into the form: "$2,000,000" and "2000000.00" are fine; anything else ("2.5",
 *  "2M") is NOT guessed at — `undefined` means "ignore this edit", so a figure can never be silently changed to another. */
export const parseAmount = (text: string): number | null | undefined => {
  const cleaned = text.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  return /^\d{1,12}(\.0+)?$/.test(cleaned) ? Number(cleaned.split(".")[0]) : undefined;
};
const showAmount = (n: number | null | undefined) => (n == null ? "" : n.toLocaleString("en-US"));

interface Props {
  company: PrimeCompany;
  onClose: () => void;
  onSaved?: (company: PrimeCompany) => void;
  /** The list changed under the form (409): reload the company and close, so reopening starts from the current list. */
  onStale?: () => void;
}

/** A form row: the credential plus a UI-only id, so a row keeps its open/closed "details" state when another is removed. */
type Row = Credential & { uid: number };

const hasDetails = (i: Row) => !!(i.detail ?? "").trim() || !!i.expires_on || i.amount_usd != null || i.aggregate_usd != null || !!i.amount_text || !!i.aggregate_text;

const blank = (kind: CredentialKind, uid: number, name = ""): Row => ({ uid, kind, name, status: "has", detail: "", expires_on: null, source: "research" });
const KIND_BADGES: Record<CredentialKind, string> = {
  certificate: "Certificate", licence: "Licence", bond: "Bond", insurance: "Insurance", vehicle: "Vehicle", business_cert: "Business cert",
};

/** The Edit credentials form: the licences and hosting/security certificates a company holds (or a person confirmed it
 *  does not). Saving counts as research when there is at least one entry you typed or confirmed, or when you tick
 *  "researched, nothing found". Entries from the CollabP sheet are shown muted: confirm the ones that are right. */
export default function EditCredentialsModal({ company, onClose, onSaved, onStale }: Props) {
  const nextUid = useRef(0);
  const [items, setItems] = useState<Row[]>(() => (company.credentials ?? []).map((c, i) => ({ ...c, uid: i })));
  nextUid.current = Math.max(nextUid.current, items.length);
  // Rows opened on existing details, or by "+ details", stay open until the form closes — emptying a field never collapses them.
  const [openDetails, setOpenDetails] = useState<Set<number>>(() => new Set(items.filter(hasDetails).map((i) => i.uid)));
  const newRow = (kind: CredentialKind, name = "") => {
    const uid = nextUid.current++;
    setItems((prev) => [...prev, blank(kind, uid, name)]);
  };
  // The "nothing found" mark means licences and certificates were researched (bonds, insurance … do not stamp it).
  const [researchedNone, setResearchedNone] = useState(
    !!company.credentials_reviewed_at && !(company.credentials ?? []).some((c) => CORE_KINDS.includes(c.kind)),
  );
  // The list the form was opened on, frozen: the save is refused if it changed since.
  const [base] = useState<Credential[]>(company.credentials ?? []);
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The backend collapses runs of spaces before it measures or de-duplicates, so the form does the same.
  const collapse = (text: string) => text.trim().replace(/\s+/g, " ");
  const named = items.filter((i) => collapse(i.name) !== "");
  const tooLong = items.some((i) => collapse(i.name).length > MAX_NAME_LENGTH || collapse(i.detail ?? "").length > MAX_DETAIL_LENGTH);
  const core = named.filter((i) => CORE_KINDS.includes(i.kind));
  // What the person typed in an amount box is kept as typed (`amount_text` / `aggregate_text`, UI-only) and only turned
  // into a figure when it is a whole-dollar number: a keystroke is never dropped or reinterpreted ("4,000,000.5" must not
  // become 40,000,005), and Save stays disabled while a box holds something that is not whole dollars.
  const invalidText = (t?: string) => t !== undefined && parseAmount(t) === undefined;
  const notWholeDollars = items.some((i) => invalidText(i.amount_text) || invalidText(i.aggregate_text));
  const badAmount = notWholeDollars || items.some((i) => (i.amount_usd ?? 0) > MAX_AMOUNT || (i.aggregate_usd ?? 0) > MAX_AMOUNT);
  const duplicate = new Set(named.map((i) => `${i.kind}:${collapse(i.name).toLowerCase()}`)).size !== named.length;

  const update = (uid: number, patch: Partial<Credential>) =>
    setItems((prev) => prev.map((it) => {
      if (it.uid !== uid) return it;
      return { ...it, ...patch };
    }));

  async function save() {
    setSaving(true);
    setError(null);
    setStale(false);
    try {
      const final: Credential[] = named.map(({ uid, amount_text, aggregate_text, ...i }) => ({ ...i, name: collapse(i.name), detail: collapse(i.detail ?? "") }));
      const updated = await saveCompanyCredentials(company.id, final, researchedNone && final.filter((i) => CORE_KINDS.includes(i.kind)).length === 0, base);
      onSaved?.(updated);
      onClose();
    } catch (err: any) {
      setStale(err?.response?.status === 409);
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const renderRow = (item: Row, index: number) => {
    const n = index + 1;
    const fromSheet = item.source === "sheet";
    const detailsOpen = openDetails.has(item.uid);
    return (
      <div key={item.uid} className={`space-y-2 rounded-lg border border-border p-2.5 ${fromSheet ? "bg-muted/40" : ""}`} data-testid="credential-item">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{KIND_BADGES[item.kind]}</span>
          <input
            value={item.name}
            onChange={(e) => update(item.uid, { name: e.target.value })}
            disabled={saving}
            maxLength={MAX_NAME_LENGTH + 20}
            placeholder={NAME_PLACEHOLDERS[item.kind]}
            aria-label={`Name of credential ${n}`}
            className={`flex-1 min-w-[12rem] px-3 py-1.5 ${FORM_CONTROL}`}
          />
          <AppSelect
            value={item.status}
            onChange={(v) => update(item.uid, { status: v as "has" | "lacks", source: v === "lacks" ? "research" : item.source })}
            disabled={saving}
            ariaLabel={`Status of credential ${n}`}
            wrapperClassName="w-44"
          >
            <option value="has">Holds</option>
            <option value="lacks">Doesn&rsquo;t hold</option>
          </AppSelect>
          {fromSheet && (
            <>
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">From sheet</span>
              <Button variant="outline" size="sm" onClick={() => update(item.uid, { source: "research" })} disabled={saving} title="Confirm this entry from the sheet" aria-label={`Confirm credential ${n}`}>
                <Check className="w-3.5 h-3.5" /> Confirm
              </Button>
            </>
          )}
          {!detailsOpen && (
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => setOpenDetails((prev) => new Set(prev).add(item.uid))}>
              + details
            </button>
          )}
          <Button variant="outline" size="sm" onClick={() => setItems((prev) => prev.filter((r) => r.uid !== item.uid))} disabled={saving} aria-label={`Remove credential ${n}`} title="Remove this credential">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
        {detailsOpen && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={item.detail ?? ""}
              onChange={(e) => update(item.uid, { detail: e.target.value })}
              disabled={saving}
              placeholder={DETAIL_PLACEHOLDERS[item.kind]}
              aria-label={`Detail of credential ${n}`}
              className={`flex-1 min-w-[12rem] px-3 py-1.5 ${FORM_CONTROL}`}
            />
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              Expires
              <input
                type="date"
                value={item.expires_on ?? ""}
                onChange={(e) => update(item.uid, { expires_on: e.target.value || null })}
                disabled={saving}
                aria-label={`Expiry date of credential ${n}`}
                className={`px-2 py-1.5 ${FORM_CONTROL}`}
              />
            </label>
            {AMOUNT_KINDS.includes(item.kind) && (
              <>
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  {item.kind === "bond" ? "Per project $" : "Per occurrence $"}
                  <input
                    inputMode="numeric"
                    value={item.amount_text ?? showAmount(item.amount_usd)}
                    onChange={(e) => { const v = parseAmount(e.target.value); update(item.uid, { amount_text: e.target.value, ...(v !== undefined ? { amount_usd: v } : {}) }); }}
                    onBlur={() => { if (!invalidText(item.amount_text)) update(item.uid, { amount_text: undefined }); }}
                    aria-invalid={invalidText(item.amount_text)}
                    disabled={saving}
                    placeholder="e.g. 2,000,000"
                    aria-label={`${item.kind === "bond" ? "Per-project amount" : "Per-occurrence limit"} of credential ${n}`}
                    className={`w-36 px-2 py-1.5 ${FORM_CONTROL}`}
                  />
                </label>
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  Aggregate $
                  <input
                    inputMode="numeric"
                    value={item.aggregate_text ?? showAmount(item.aggregate_usd)}
                    onChange={(e) => { const v = parseAmount(e.target.value); update(item.uid, { aggregate_text: e.target.value, ...(v !== undefined ? { aggregate_usd: v } : {}) }); }}
                    onBlur={() => { if (!invalidText(item.aggregate_text)) update(item.uid, { aggregate_text: undefined }); }}
                    aria-invalid={invalidText(item.aggregate_text)}
                    disabled={saving}
                    placeholder="e.g. 4,000,000"
                    aria-label={`Aggregate amount of credential ${n}`}
                    className={`w-36 px-2 py-1.5 ${FORM_CONTROL}`}
                  />
                </label>
              </>
            )}
            {AMOUNT_KINDS.includes(item.kind) && (
              <p className="basis-full text-[11px] text-muted-foreground">Whole dollars only (no decimals, no &ldquo;M&rdquo; or &ldquo;K&rdquo;). Leave empty when the amount is not known.</p>
            )}
          </div>
        )}
        {(collapse(item.detail ?? "").length > MAX_DETAIL_LENGTH || collapse(item.name).length > MAX_NAME_LENGTH) && (
          <p className="text-[11px] text-destructive">Name up to {MAX_NAME_LENGTH} characters, detail up to {MAX_DETAIL_LENGTH}.</p>
        )}
      </div>
    );
  };

  return (
    <Modal
      size="lg"
      modalClassName="h-[85%]"
      title="Edit credentials"
      description={company.name || company.domain || "company"}
      icon={<BadgeCheck />}
      onClick={saving ? undefined : onClose}
      closeButtonCLick={saving ? undefined : onClose}
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving || tooLong || duplicate || badAmount}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <span className="mt-0.5 shrink-0 cursor-help" title="Pick a suggested name from the dropdown; names are matched strictly, so a typed name only matches the same words. Choose &quot;Doesn't hold&quot; only when confirmed: for a licence or certificate it is the only status that can rule a company out; bonds, insurance, vehicles and business certifications never do (a gap is a Hold or a condition). Amounts are whole dollars. A credential past its expiry date counts as unknown. Entries from the CollabP sheet are unconfirmed until you press Confirm." aria-label="How credentials are used" role="img">
            <Info className="h-3.5 w-3.5" />
          </span>
          <span>Pick a credential to add, or choose Other… to type your own. Hover the info icon for the rules.</span>
        </p>
        <CredentialPicker
          kinds={KIND_ORDER}
          disabled={saving || items.length >= MAX_CREDENTIALS}
          taken={new Set(items.map((i) => `${i.kind}:${collapse(i.name).toLowerCase()}`))}
          onPick={(kind, name) => newRow(kind, name ?? "")}
        />
        {items.length === 0 && <p className="text-sm text-muted-foreground">No credentials yet. Use &ldquo;Add credential&rdquo; above.</p>}
        <div className="space-y-2">{items.map(renderRow)}</div>
        {duplicate && <p className="text-[11px] text-destructive">Two entries have the same kind and name.</p>}
        {notWholeDollars && <p className="text-[11px] text-destructive">An amount must be a whole number of dollars (no decimals, no &ldquo;M&rdquo; or &ldquo;K&rdquo;).</p>}
        {!notWholeDollars && badAmount && <p className="text-[11px] text-destructive">An amount is above the maximum ($1 trillion).</p>}
        <label className={`flex items-center gap-2 text-sm ${saving || core.length > 0 ? "text-muted-foreground opacity-60 cursor-not-allowed" : "text-foreground cursor-pointer"}`}>
          <input
            type="checkbox"
            checked={researchedNone && core.length === 0}
            disabled={saving || core.length > 0}
            onChange={(e) => setResearchedNone(e.target.checked)}
            className="accent-primary"
          />
          Licences and certificates: researched — nothing found
        </label>
      </div>
      {error && (
        <Alert tone="danger" className="mt-4">
          {error}
          {stale && onStale && (
            <button type="button" onClick={onStale} className="ml-2 font-medium underline">
              Reload latest
            </button>
          )}
        </Alert>
      )}
    </Modal>
  );
}
